import { LitElement, html, nothing } from "lit";
import { fa, onceParentAvailable } from "../../../utils/widgets.js";
import { DISABLED_LEVEL } from "../../../utils/indoor.js";
import {
	faCaretDown, faCaretUp, faTimes
} from "@fortawesome/free-solid-svg-icons";
import LevelSelectStyles from "./LevelSelect.css" with { type: "css" };
import ButtonGroupStyles from "../ButtonGroup.css" with { type: "css" };
document.adoptedStyleSheets.push(LevelSelectStyles);
document.adoptedStyleSheets.push(ButtonGroupStyles);

/**
 * Widget for indoor level selecting over map.
 * It relies on indoor= MapLibre plugin to work, so this should be enabled before calling this widget.
 * @class Panoramax.components.ui.widgets.LevelSelect
 * @element pnx-level-select
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-level-select _parent=${viewer} />
 * ```
 */
export default class LevelSelect extends LitElement {
	/** @private */
	static properties = {
		_expanded: {state: true},
	};

	/** @private */
	createRenderRoot() {
		return this;
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();
		onceParentAvailable(this)
			.then(this._parent.onceMapReady?.())
			.then(() => {
				this._parent.map.indoorEqual.on("levelchange", () => this.requestUpdate());
				this._parent.map.indoorEqual.on("levelschange", () => this.requestUpdate());
			});
	}

	/** @private */
	updated(changedProps) {
		super.updated(changedProps);

		// Make level list scroll to see currently selected level
		if(this._expanded) {
			if(changedProps?.has("_expanded")) {
				this._scrollToCurrentLevel(false, "instant");
			} else {
				this._scrollToCurrentLevel(true, "smooth");
			}
		}
	}

	/** @private */
	_scrollToCurrentLevel(immediate, behavior) {
		const lvlSelected = this.renderRoot.querySelector(".pnx-level-active");
		setTimeout(
			() => lvlSelected?.scrollIntoView({behavior, block: "center", container: "nearest"}),
			immediate ? 0 : 50
		);
	}

	/** @private */
	render() {
		/* eslint-disable eqeqeq */
		const ieq = this._parent.map.indoorEqual;

		// Disabled level = hide
		if(ieq.level === DISABLED_LEVEL || ieq.levels.length === 0) { return nothing; }

		// Find upper/lower level
		// Note that levels are reverse sorted
		const lvlId = ieq.levels.findIndex(l => l == ieq.level);
		const lvlUpId = lvlId > 0 ? lvlId - 1 : null;
		const lvlUp = lvlUpId !== null ? ieq.levels[lvlUpId] : null;
		const lvlDownId = lvlId < ieq.levels.length - 1 ? lvlId + 1 : null;
		const lvlDown = lvlDownId !== null ? ieq.levels[lvlDownId] : null;

		return html`
			<pnx-button-group
				dir="column"
				size="xl"
				class="pnx-print-hidden"
			>
				<pnx-button
					kind="superflat"
					@click=${() => ieq.setLevel(lvlUp)}
					.disabled=${lvlUp === null}
					class="pnx-level-minimal"
					title=${this._parent?._t?.pnx.indoor_selector_levelup}
				>${fa(faCaretUp)}</pnx-button>

				${this._expanded ? html`
					<pnx-button-group
						dir="column"
						size="xl"
						class="pnx-print-hidden pnx-level-scroller"
					>
						${ieq.levels.map(l => html`
							<pnx-button
								kind="superflat"
								@click=${() => ieq.setLevel(l)}
								class=${l.length > 2 ? "pnx-level-long" : null}
							>
								${ieq.level == l ? html`<span class="pnx-level-active">${l}</span>`: l}
							</pnx-button>
						`)}
					</pnx-button-group>
				` : html`
					<pnx-button
						kind="superflat"
						class="pnx-level-minimal"
						title=${this._parent?._t?.pnx.indoor_selector_expand}
						@click=${ieq.levels.length > 1 ? () => this._expanded = true : null}
					>${ieq.level}</pnx-button>
				`}

				<pnx-button
					kind="superflat"
					@click=${() => ieq.setLevel(lvlDown)}
					.disabled=${lvlDown === null}
					class="pnx-level-minimal"
					title=${this._parent?._t?.pnx.indoor_selector_leveldown}
				>${fa(faCaretDown)}</pnx-button>

				${this._expanded ? html`
					<pnx-button
						kind="flat"
						class="pnx-level-shrink"
						@click=${() => this._expanded = false}
						title=${this._parent?._t?.pnx.indoor_selector_shrink}
					>${fa(faTimes)}</pnx-button>
				` : nothing}
			</pnx-button-group>
		`;
	}
}

customElements.define("pnx-level-select", LevelSelect);
